<script setup>
import { ref, computed } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import {
    PhBookOpen, PhPrinter, PhCaretDown, PhMedal,
    PhCheckCircle, PhWarningCircle, PhArrowDown,
} from '@phosphor-icons/vue'
import Layout  from '@pages/Shared/layouts/StudentLayout.vue'

const props = defineProps({
    student:        Object,
    sessions:       Array,
    terms:          Array,
    activeSession:  Object,
    activeTerm:     Object,
    selectedSession: Object,
    selectedTerm:    Object,
    result:          Object,  // { subjects: [], summary: {total, average, position, grade, remark, published} }
    classRoom:       Object,
})

const sessionId = ref(props.selectedSession?.id ?? props.activeSession?.id)
const termId    = ref(props.selectedTerm?.id    ?? props.activeTerm?.id)

const applyFilters = () => {
    router.get('/student/results', {
        session_id: sessionId.value,
        term_id:    termId.value,
    }, { preserveState: true, preserveScroll: true })
}

const gradeColor = (grade) => ({
    A: 'text-green-600 bg-green-50',
    B: 'text-primary-600 bg-primary-50',
    C: 'text-yellow-600 bg-yellow-50',
    D: 'text-orange-600 bg-orange-50',
    E: 'text-red-500 bg-red-50',
    F: 'text-red-700 bg-red-100',
}[grade] ?? 'text-gray-500 bg-gray-100')

const scoreColor = (score) => {
    if (score >= 70) return 'text-green-600'
    if (score >= 50) return 'text-yellow-600'
    return 'text-red-500'
}

const printResult = () => {
    window.print()
}
</script>

<template>
    <Layout>
        <Head title="Results" />

        <!-- Header -->
        <div class="flex items-start justify-between mb-8 flex-wrap gap-4">
            <div>
                <h1 class="text-2xl font-bold text-gray-900">Term Results</h1>
                <p class="text-gray-500 text-sm mt-1">View and print your academic results</p>
            </div>
            <button
                v-if="result?.summary?.published"
                @click="printResult"
                class="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200
                       text-gray-700 hover:bg-gray-50 text-sm font-medium transition print:hidden">
                <PhPrinter :size="16" />
                Print Result
            </button>
        </div>

        <!-- Filters -->
        <div class="flex items-center gap-3 mb-6 flex-wrap print:hidden">
            <select v-model="sessionId" @change="applyFilters"
                class="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white
                       focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                <option v-for="s in sessions" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <select v-model="termId" @change="applyFilters"
                class="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white
                       focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                <option v-for="t in terms" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
        </div>

        <!-- Not published -->
        <div v-if="!result?.summary?.published"
            class="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div class="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <PhBookOpen :size="24" class="text-gray-400" />
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Result Not Yet Published</h3>
            <p class="text-sm text-gray-400">
                Results for this term have not been published yet. Check back later.
            </p>
        </div>

        <!-- Result sheet -->
        <template v-else>

            <!-- Summary cards -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div v-for="item in [
                    { label: 'Total Score',  value: result.summary.total,    unit: ''  },
                    { label: 'Average',      value: result.summary.average,  unit: '%' },
                    { label: 'Position',     value: result.summary.position, unit: result.summary.position_suffix ?? '' },
                    { label: 'Grade',        value: result.summary.grade,    unit: ''  },
                ]" :key="item.label"
                    class="bg-white rounded-lg border border-gray-200 p-4 text-center">
                    <p class="text-xs text-gray-400 mb-1">{{ item.label }}</p>
                    <p class="text-xl font-bold text-gray-900">
                        {{ item.value ?? '—' }}<span class="text-sm font-medium text-gray-400">{{ item.unit }}</span>
                    </p>
                </div>
            </div>

            <!-- Remark banner -->
            <div class="mb-6 p-4 rounded-lg border flex items-center gap-3"
                :class="result.summary.average >= 50
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'">
                <PhCheckCircle v-if="result.summary.average >= 50"
                    :size="20" class="text-green-600 shrink-0" />
                <PhWarningCircle v-else :size="20" class="text-red-500 shrink-0" />
                <div class="text-sm">
                    <span class="font-semibold"
                        :class="result.summary.average >= 50 ? 'text-green-800' : 'text-red-700'">
                        {{ result.summary.remark }}
                    </span>
                    <span class="text-gray-500 ml-2">
                        — {{ selectedTerm?.name ?? activeTerm?.name }},
                        {{ selectedSession?.name ?? activeSession?.name }}
                    </span>
                </div>
            </div>

            <!-- Score table -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div class="px-5 py-4 border-b border-gray-100">
                    <h3 class="font-semibold text-gray-800 text-sm">Subject Scores</h3>
                </div>
                <div class="overflow-x-auto">
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="bg-gray-50 text-left">
                                <th class="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Subject</th>
                                <th v-for="h in result.subjects?.[0]?.assessments ?? []"
                                    :key="h.name"
                                    class="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">
                                    {{ h.name }}
                                </th>
                                <th class="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Total</th>
                                <th class="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Grade</th>
                                <th class="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Remark</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50">
                            <tr v-for="row in result.subjects" :key="row.subject_id"
                                class="hover:bg-gray-50 transition">
                                <td class="px-5 py-3.5 font-medium text-gray-800">{{ row.subject_name }}</td>
                                <td v-for="score in row.assessments" :key="score.name"
                                    class="px-3 py-3.5 text-center text-gray-600">
                                    {{ score.value ?? '—' }}
                                </td>
                                <td class="px-3 py-3.5 text-center font-bold"
                                    :class="scoreColor(row.total)">
                                    {{ row.total ?? '—' }}
                                </td>
                                <td class="px-3 py-3.5 text-center">
                                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold"
                                        :class="gradeColor(row.grade)">
                                        {{ row.grade ?? '—' }}
                                    </span>
                                </td>
                                <td class="px-3 py-3.5 text-center text-gray-500 text-xs">
                                    {{ row.remark ?? '—' }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Print footer info -->
            <div class="hidden print:block mt-8 text-center text-xs text-gray-400 space-y-1">
                <p>Bn Mas'ud College for Tahfiz and Islamic Studies</p>
                <p>{{ selectedSession?.name ?? activeSession?.name }} · {{ selectedTerm?.name ?? activeTerm?.name }}</p>
                <p>Student: {{ student.first_name }} {{ student.last_name }} · ID: {{ student.id }}</p>
            </div>
        </template>
    </Layout>
</template>

<style>
@media print {
    header, footer, .print\:hidden { display: none !important; }
    body { margin: 0; }
}
</style>